import React from 'react';
import { useSearchParams } from 'react-router-dom';
import RazzmatazzWorkspace from './RazzmatazzWorkspace';
import './RazzmatazzEmbedPage.css';

const THEME_VARS: Record<'light' | 'dark', Record<string, string>> = {
  dark: {
    '--color-canvas': '#0b1220',
    '--color-surface': '#243044',
    '--color-surface-muted': '#2f4058',
    '--color-text': '#f8fafc',
    '--color-text-primary': '#f8fafc',
    '--color-text-strong': '#ffffff',
    '--color-text-muted': '#d7e0eb',
    '--color-border': '#6b7c93',
    '--color-border-strong': '#8b9bb0',
    '--color-control-bg': '#1a2436',
    '--color-accent': '#79b8ff',
    '--color-primary': '#79b8ff',
    '--color-primary-strong': '#a5d0ff',
    '--color-success': '#3dd68c',
    '--shadow-soft': '0 10px 28px rgba(0, 0, 0, 0.45)',
  },
  light: {
    '--color-canvas': '#f4f1ea',
    '--color-surface': '#ffffff',
    '--color-surface-muted': '#ebe6dc',
    '--color-text': '#1c1917',
    '--color-text-primary': '#1c1917',
    '--color-text-strong': '#0c0a09',
    '--color-text-muted': '#44403c',
    '--color-border': '#a8a29e',
    '--color-border-strong': '#78716c',
    '--color-control-bg': '#ffffff',
    '--color-accent': '#1a73e8',
    '--color-primary': '#1a73e8',
    '--color-primary-strong': '#0f4fad',
    '--color-success': '#15803d',
    '--shadow-soft': '0 10px 24px rgba(19, 48, 91, 0.14)',
  },
};

function applyTheme(root: HTMLElement, theme: 'light' | 'dark', overrides?: Record<string, string>) {
  const vars = { ...THEME_VARS[theme], ...(overrides || {}) };
  Object.entries(vars).forEach(([key, value]) => {
    if (/^--[a-z0-9-]+$/i.test(key) && typeof value === 'string' && !/[<>'"`]|url\(|expression\(/i.test(value)) {
      root.style.setProperty(key, value);
    }
  });
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
}

const RazzmatazzEmbedPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = (params.get('token') || '').trim();
  const themeParam = (params.get('theme') || 'dark').toLowerCase() === 'light' ? 'light' : 'dark';
  const projectHint = (params.get('project') || 'GossLed').trim() || 'GossLed';
  const omitTraffic = ['1', 'true', 'yes'].includes((params.get('omitTraffic') || '').trim().toLowerCase());
  const omitChrome = ['1', 'true', 'yes'].includes((params.get('omitChrome') || '').trim().toLowerCase());
  const initialPane = (params.get('pane') || '').trim();
  // Optional host= filter from the host shell. Do not invent a host — empty means all hosts.
  const trafficHost = React.useMemo(() => {
    const fromQuery = (params.get('host') || params.get('trafficHost') || '').trim().toLowerCase();
    return fromQuery ? fromQuery.replace(/^www\./, '') : '';
  }, [params]);
  const shell = React.useRef<HTMLDivElement | null>(null);
  const [error, setError] = React.useState('');

  const gatewayBaseUrl = React.useMemo(() => {
    // Same apex as the marketing site (nextcompli.com) proxies /api to the gateway.
    if (typeof window === 'undefined') return '';
    const host = window.location.hostname.replace(/^www\./, '');
    if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
      return 'http://127.0.0.1:8000';
    }
    return `https://${host}`.replace(/\/$/, '');
  }, []);

  React.useEffect(() => {
    if (!token) {
      setError('This Razzmatazz window needs an embed token from the host site.');
    }
  }, [token]);

  React.useEffect(() => {
    const root = document.documentElement;
    applyTheme(root, themeParam);
    function onMessage(event: MessageEvent) {
      if (event.source !== window.parent) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type !== 'razzmatazz:theme' && data.type !== 'postlude:theme') return;
      const nextTheme = data.theme === 'light' ? 'light' : 'dark';
      const cssVariables =
        data.cssVariables && typeof data.cssVariables === 'object'
          ? (data.cssVariables as Record<string, string>)
          : undefined;
      applyTheme(root, nextTheme, cssVariables);
    }
    window.addEventListener('message', onMessage);
    window.parent.postMessage(
      {
        type: 'razzmatazz:ready',
        theme: themeParam,
        project: projectHint,
        hostChrome: omitChrome,
        omitTraffic,
      },
      '*',
    );
    return () => window.removeEventListener('message', onMessage);
  }, [themeParam, projectHint, omitChrome, omitTraffic]);

  React.useEffect(() => {
    const node = shell.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const report = () => {
      const height = Math.ceil(node.getBoundingClientRect().height);
      window.parent.postMessage({ type: 'razzmatazz:height', height }, '*');
    };
    report();
    const observer = new ResizeObserver(report);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div className={`razzmatazz-embed${omitChrome ? ' is-pane-only' : ''}`} ref={shell} data-theme={themeParam}>
      {error ? (
        <p className="razzmatazz-embed__error" role="alert">
          {error}
        </p>
      ) : (
        <RazzmatazzWorkspace
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={token}
          language="en"
          preferredProjectName={projectHint}
          trafficHost={trafficHost}
          embedded
          omitTraffic={omitTraffic}
          omitChrome={omitChrome}
          initialPane={initialPane || undefined}
          scopeChrome="minimal"
        />
      )}
    </div>
  );
};

export default RazzmatazzEmbedPage;
